import FormatQuote from "@mui/icons-material/FormatQuote";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import { useEffect, useState } from "react";
import Carousel from "react-material-ui-carousel";

import { kickBlue } from "~/shared/styles/baseStyles.js";
import { testimonialsForConsultationType } from "~/shared/widgets/testimonials.js";

// FONTS_LOADED / FONTS_PENDING are used as the Carousel's `key`, to force ONE remount
// once webfonts have settled. This works around a measurement bug in
// react-material-ui-carousel@3.4.2 that clips the last line of the active slide.
//
// The library measures the active slide's offsetHeight once and writes it as an inline
// height on a wrapper that is `overflow: hidden` (CarouselItem.js: checkAndSetHeight).
// Its effect depends only on [setHeight, state.active, index, divRef], so it re-measures
// when the SLIDE changes and never when the CONTENT reflows.
//
// That loses a race on first paint: the carousel measures while the paragraph is still
// laid out in the fallback system font, the custom face then loads and reflows the text
// onto an extra wrapped line, and nothing re-measures -- so the stale height shears off
// the attribution name. It shows on mobile because the narrow column is where a
// font-metric change actually flips a line count; desktop absorbs the same delta.
//
// Changing the key remounts the carousel, which re-runs the measurement against the real
// fonts. Remounting resets to the first slide, which is harmless here: it happens within
// a few hundred ms of mount and autoPlay is off, so there is no position to lose. When
// the fonts are already cached the key starts at FONTS_LOADED and no remount ever
// happens.
//
// If this dependency is upgraded, check whether the height effect observes content
// changes; if it does, this can go.
//
// The key also carries consultationType, so if the list ever changed on a mounted
// carousel the height would be re-measured for the new content as well.
const FONTS_LOADED = "fonts-loaded";
const FONTS_PENDING = "fonts-pending";

const fontsAlreadyLoaded = () =>
  typeof document !== "undefined" && document.fonts
    ? document.fonts.status === "loaded"
    : true;

export default function TestimonialsCarousel({ consultationType }) {
  const [fontKey, setFontKey] = useState(() =>
    fontsAlreadyLoaded() ? FONTS_LOADED : FONTS_PENDING,
  );

  useEffect(() => {
    if (fontKey === FONTS_LOADED) {
      return undefined;
    }
    // No FontFaceSet (older browsers, jsdom): nothing to wait for, so settle now rather
    // than leaving the carousel keyed as pending forever.
    if (typeof document === "undefined" || !document.fonts) {
      setFontKey(FONTS_LOADED);
      return undefined;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) {
        setFontKey(FONTS_LOADED);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fontKey]);

  const items = testimonialsForConsultationType(consultationType);

  return (
    <Carousel
      key={`${fontKey}:${consultationType || ""}`}
      swipe={true}
      animation="slide"
      navButtonsAlwaysInvisible={true}
      interval={5000}
      autoPlay={false}
      navButtonsProps={{
        style: {
          backgroundColor: "transparent",
          color: "rgba(150, 150, 150, 0.5)",
          padding: "0",
          fontSize: "55px",
          margin: "0",
        },
      }}
      NextIcon={<NavigateNext fontSize="large" />}
      PrevIcon={
        <NavigateBefore fontSize="large" style={{ marginRight: "-25" }} />
      }
    >
      {items.map((item, i) => (
        <Item key={i} item={item} />
      ))}
    </Carousel>
  );
}

function Item(props) {
  return (
    <div>
      <FormatQuote
        style={{
          color: kickBlue,
          transform: "scale(-1)",
          height: "35px",
          width: "35px",
        }}
        fontSize="large"
      />
      <p>{props.item.description}</p>
      <div
        style={{
          fontWeight: "bold",
        }}
      >
        {props.item.name}
      </div>
    </div>
  );
}
